import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import MobileMenu from '../../components/feature/MobileMenu';
import { useSEO } from '../../hooks/useSEO';
import JsonLd from '../../components/feature/JsonLd';

export default function ResourcesPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useSEO({
    title: 'Resources & Insights | Design Thinking, Innovation & Impact | Expert Knowledge',
    description: 'Access our collection of design thinking resources, innovation frameworks, and insights from human-centred design projects. Learn from our experience in healthcare, government, and social impact.',
    keywords: 'design resources, innovation insights, design thinking guides, case study insights, design frameworks, innovation tools',
    canonical: '/resources',
    ogType: 'website',
    ogImage: 'https://public.readdy.ai/ai/img_res/3f7812b4fc25eba7fae7139a3340bfec.jpg'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Resources & Insights",
        "url": "https://example.com/resources",
        "description": "Design thinking resources, innovation frameworks, and insights from human-centred design projects",
        "dateModified": "2025-06-01",
        "publisher": {
          "@type": "Organization",
          "name": "Design For",
          "url": "https://example.com",
          "logo": "https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/511aeba7affe24966a38635cdd087c13.png"
        }
      }} />
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/511aeba7affe24966a38635cdd087c13.png"
                alt="Design For"
                className="h-16"
              />
            </Link>
            <div className="hidden md:flex items-center space-x-10">
              <Link
                to="/"
                className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider"
              >
                Home
              </Link>
              <Link
                to="/about"
                className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider"
              >
                About
              </Link>
              <Link
                to="/services"
                className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider"
              >
                Services
              </Link>
              <Link
                to="/work"
                className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider"
              >
                Work
              </Link>
              <Link
                to="/resources"
                className="text-[#ff4858] hover:text-[#ff4858] transition-colors duration-200 text-sm font-medium uppercase tracking-wider"
              >
                Insights
              </Link>
              <Link
                to="/contact"
                className="bg-primary text-white px-6 py-3 rounded-full hover:bg-primary/90 transition-all duration-200 text-sm font-bold uppercase tracking-wider whitespace-nowrap"
              >
                Contact
              </Link>
            </div>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden text-black"
            >
              <i className="ri-menu-line text-3xl"></i>
            </button>
          </div>
        </div>
      </nav>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Hero Section */}
      <section className="pt-40 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h1 className="text-7xl lg:text-8xl font-black text-black tracking-tight leading-[0.95] mb-10">
            Coming <span className="text-black">soon!</span>
          </h1>
          <p className="text-2xl lg:text-3xl text-black font-medium leading-relaxed max-w-5xl">
            Articles, perspectives and thought leadership on strategy, experience design
            and innovation — written from the frontlines of real client work.
          </p>
        </div>
      </section>

      {/* Image & CTA Section */}
      <section className="py-24 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="w-full h-[520px] rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/c104c2178aaf92062f923c0c000ce181.jpeg"
                  alt="Design thinking workshop with sticky notes and sketches on a table"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Decorative accent */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#ff4858]/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#ff4858]/5 rounded-2xl -z-10"></div>
            </div>

            {/* Content */}
            <div className="space-y-10">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-3 bg-[#ff4858]/10 px-5 py-2.5 rounded-full">
                  <span className="w-2 h-2 bg-[#ff4858] rounded-full animate-pulse"></span>
                  <span className="text-sm font-bold text-[#ff4858] uppercase tracking-wider">
                    In the works
                  </span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight">
                  Articles &amp; thought leadership are on their way
                </h2>
                <p className="text-lg text-gray-500 font-medium leading-relaxed">
                  Ideas, frameworks and hard-won lessons from years of
                  strategy and design work — so you can think differently about the
                  challenges your organisation is facing.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#ff4858]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-article-line text-[#ff4858] text-lg"></i>
                  </div>
                  <p className="text-base font-bold text-black">
                    In-depth articles on strategy &amp; experience design
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#ff4858]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-discuss-line text-[#ff4858] text-lg"></i>
                  </div>
                  <p className="text-base font-bold text-black">
                    Perspectives on innovation &amp; human-centred design
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#ff4858]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-[#ff4858] text-lg"></i>
                  </div>
                  <p className="text-base font-bold text-black">
                    Lessons from real client work across sectors
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Can't Wait CTA */}
      <section className="py-28 bg-[#ff4858]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl lg:text-6xl font-black text-white tracking-tight mb-6">
            Can&apos;t wait?
          </h2>
          <p className="text-xl text-white/90 mb-12 font-medium max-w-2xl mx-auto">
            Follow me on LinkedIn for regular insights, perspectives and behind-the-scenes
            thinking on strategy, experience design and innovation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://www.linkedin.com/in/kara-prosser-26960977/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#ff4858] px-12 py-5 rounded-full hover:bg-white/90 transition-all duration-300 text-base font-bold uppercase tracking-wider inline-flex items-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-linkedin-fill text-xl"></i>
              Follow on LinkedIn
            </a>
            <Link
              to="/contact"
              className="bg-white text-[#ff4858] px-12 py-5 rounded-full hover:bg-white/90 transition-all duration-300 text-base font-bold uppercase tracking-wider inline-flex items-center gap-3 whitespace-nowrap cursor-pointer"
            >
              Let&apos;s Chat
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">
                Strategic consulting for ambitious businesses.
              </p>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">
                Navigate
              </h4>
              <div className="space-y-3">
                <Link
                  to="/"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  About
                </Link>
                <Link
                  to="/services"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Services
                </Link>
                <Link
                  to="/work"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Work
                </Link>
                <Link
                  to="/resources"
                  className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium"
                >
                  Insights
                </Link>
                <Link
                  to="/contact"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Contact
                </Link>
              </div>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">
                Follow
              </h4>
              <div className="flex gap-4">
                <a
                  href="https://www.linkedin.com/in/kara-prosser-26960977/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</Link>
              <Link to="/terms" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</Link>
              <Link to="/cookies" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
